// internal
import { TypeAssert } from '@creejs/commons-lang'
// owned
import LogLevel from './log-level.js'

const { Level, DefaultLevel, assertLevel } = LogLevel
const { assertString } = TypeAssert
/**
 * Abstract Logger Class of All Logger
 * @abstract
 */
export default class Logger {
  /**
   * Creates a new Logger instance.
   * @constructor
   * @param {string} name - The name identifier for the logger
   * @param {*} [nativeLogger]
   * @param {number} [level] - The logging level, default is Level.ERROR=1
   */
  constructor (name, nativeLogger, level = DefaultLevel) {
    assertString(name)
    assertLevel(level)
    this._name = name
    this._nativeLogger = nativeLogger
    this._level = level
  }

  get nativeLogger () {
    return this._nativeLogger
  }

  get level () {
    return this._level
  }

  /**
   * Gets the name of the logger instance.
   * @returns {string} The name of the logger.
   */
  get name () {
    return this._name
  }

  /**
   * Checks if FATAL level logging is enabled for this logger.
   * @returns {boolean} True if FATAL level logging is enabled, false otherwise.
   */
  get fatalEnabled () {
    return this.level >= Level.FATAL
  }

  /**
   * Checks if ERROR level logging is enabled for this logger.
   * @returns {boolean} True if ERROR level logging is enabled, false otherwise.
   */
  get errorEnabled () {
    return this.level >= Level.ERROR
  }

  /**
   * Checks if WARN level logging is enabled for this logger.
   * @returns {boolean} True if WARN level logging is enabled, false otherwise.
   */
  get warnEnabled () {
    return this.level >= Level.WARN
  }

  /**
   * Checks if DEBUG level logging is enabled for this logger.
   * @returns {boolean} True if DEBUG level logging is enabled, false otherwise.
   */
  get debugEnabled () {
    return this.level >= Level.DEBUG
  }

  /**
   * Checks if INFO level logging is enabled for this logger.
   * @returns {boolean} True if INFO level logging is enabled, false otherwise.
   */
  get infoEnabled () {
    return this.level >= Level.INFO
  }

  /**
   * Checks if TRACE level logging is enabled for this logger.
   * @returns {boolean} True if TRACE level logging is enabled, false otherwise.
   */
  get traceEnabled () {
    return this.level >= Level.TRACE
  }

  /**
   * change log level for current logger instance.
   * @param {number} level - new log level to set
   * @throws {Error} Currently throws an error as this method is Not Impled Yet.
   * @abstract
   */
  setLevel (level) {
    LogLevel.assertLevel(level)
    this._setLevel(level)
    this._level = level
  }

  /**
   * Logs a fatal error message with timestamp and logger name.
   * Only outputs if fatal logging is enabled for this logger instance.
   * @param {...any} args - Arguments to log (will be space-separated)
   */
  fatal (...args) {
    if (this.fatalEnabled) {
      this._fatal(...args)
    }
  }

  /**
   * Logs an error message to the console with timestamp and logger name.
   * Only logs if error logging is enabled for this logger instance.
   * @param {...any} args - Arguments to be logged as error message
   */
  error (...args) {
    if (this.errorEnabled) {
      this._error(...args)
    }
  }

  /**
   * Logs a warning message to the console if warn logging is enabled.
   * @param {...any} args - The arguments to log as a warning message.
   */
  warn (...args) {
    if (this.warnEnabled) {
      this._warn(...args)
    }
  }

  /**
   * Logs debug messages to console if debug mode is enabled.
   * @param {...any} args - The data to be logged
   */
  debug (...args) {
    if (this.debugEnabled) {
      this._debug(...args)
    }
  }

  /**
   * Logs an info message to the console with timestamp and logger name.
   * @param {...any} args - The data to be logged. Accepts multiple arguments.
   */
  info (...args) {
    if (this.infoEnabled) {
      this._info(...args)
    }
  }

  /**
   * Logs a trace message with timestamp and logger name if trace logging is enabled.
   * @param {...any} args - Data to be logged as trace message.
   */
  trace (...args) {
    if (this.traceEnabled) {
      this._trace(...args)
    }
  }

  /**
   * Checks if FATAL level logging is enabled for this logger.
   * @returns {boolean} True if FATAL level logging is enabled, false otherwise.
   */
  isFatalEnabled () {
    return this.fatalEnabled
  }

  /**
   * Checks if ERROR level logging is enabled for this logger.
   * @returns {boolean} True if ERROR level logging is enabled, false otherwise.
   */
  isErrorEnabled () {
    return this.errorEnabled
  }

  /**
   * Checks if WARN level logging is enabled for this logger.
   * @returns {boolean} True if WARN level logging is enabled, false otherwise.
   */
  isWarnEnabled () {
    return this.warnEnabled
  }

  /**
   * Checks if DEBUG level logging is enabled for this logger.
   * @returns {boolean} True if DEBUG level logging is enabled, false otherwise.
   */
  isDebugEnabled () {
    return this.debugEnabled
  }

  /**
   * Checks if INFO level logging is enabled for this logger.
   * @returns {boolean} True if INFO level logging is enabled, false otherwise.
   */
  isInfoEnabled () {
    return this.infoEnabled
  }

  /**
   * Checks if TRACE level logging is enabled for this logger.
   * @returns {boolean} True if TRACE level logging is enabled, false otherwise.
   */
  isTraceEnabled () {
    return this.traceEnabled
  }

  /**
   * Sets the logging level
   * @param {number} level
   * @throws {Error} Always throws "Not Impled Yet Yet" error.
   * @protected
   * @abstract
   */
  _setLevel (level) {
    throw new Error('Not Impled Yet')
  }

  /**
   * Override this method to implement fatal logging.
   * @protected
   * @param {...*} args - Variable arguments
   * @throws {Error} Always throws "Not Impled Yet Yet" error
   */
  _fatal (...args) {
    throw new Error('Not Impled Yet')
  }

  /**
   * Override this method to implement error logging.
   * @protected
   * @param {...*} args - Variable arguments
   * @throws {Error} Always throws "Not Impled Yet Yet" error
   */
  _error (...args) {
    throw new Error('Not Impled Yet')
  }

  /**
   * Override this method to implement warn logging.
   * @protected
   * @param {...*} args - Variable arguments
   * @throws {Error} Always throws "Not Impled Yet Yet" error
   */
  _warn (...args) {
    throw new Error('Not Impled Yet')
  }

  /**
   * Override this method to implement debug logging.
   * @protected
   * @param {...*} args - Variable arguments
   * @throws {Error} Always throws "Not Impled Yet Yet" error
   */
  _debug (...args) {
    throw new Error('Not Impled Yet')
  }

  /**
   * Override this method to implement info logging.
   * @protected
   * @param {...*} args - Variable arguments
   * @throws {Error} Always throws "Not Impled Yet Yet" error
   */
  _info (...args) {
    throw new Error('Not Impled Yet')
  }

  /**
   * Override this method to implement trace logging.
   * @protected
   * @param {...*} args - Variable arguments
   * @throws {Error} Always throws "Not Impled Yet Yet" error
   */
  _trace (...args) {
    throw new Error('Not Impled Yet')
  }
}
